import type { Action } from "@/components/MenuButton";

export const QUICK_ACTION_MENU_ANALYTICS_EVENTS = {
  clickMenuItem: "click_quick_action_menu_item"
} as const;

export type QuickActionMenuSource = "person_contact" | "global_add";

export type QuickActionMenuAction = "call" | "text" | "email" | "add_interaction" | "add_task" | "add_note";

export type QuickActionMenuAnalyticsPayload = {
  menuSource: QuickActionMenuSource;
  action: QuickActionMenuAction;
  pgaId?: string;
  phoneType?: "mobile" | "home";
};

const CONTACT_MENU_ACTION_BY_TEST_ID: Record<string, Pick<QuickActionMenuAnalyticsPayload, "action" | "phoneType">> = {
  "contact-call-mobile": { action: "call", phoneType: "mobile" },
  "contact-call-home": { action: "call", phoneType: "home" },
  "contact-text-mobile": { action: "text", phoneType: "mobile" },
  "contact-text-home": { action: "text", phoneType: "home" },
  "contact-email": { action: "email" },
  "contact-add-interaction": { action: "add_interaction" },
  "contact-add-task": { action: "add_task" }
};

const GLOBAL_ADD_MENU_ACTION_BY_TEST_ID: Record<string, QuickActionMenuAction> = {
  "add-interaction-menu-item": "add_interaction",
  "add-note-menu-item": "add_note",
  "add-task-menu-item": "add_task"
};

export function getPersonContactMenuAnalyticsPayload(
  testId: string | undefined,
  pgaId: string
): QuickActionMenuAnalyticsPayload | null {
  if (!testId) return null;

  const actionPayload = CONTACT_MENU_ACTION_BY_TEST_ID[testId];
  if (!actionPayload) return null;

  return {
    menuSource: "person_contact",
    pgaId,
    ...actionPayload
  };
}

export function getGlobalAddMenuAnalyticsPayload(
  dataTestId: string | undefined
): QuickActionMenuAnalyticsPayload | null {
  if (!dataTestId) return null;

  const action = GLOBAL_ADD_MENU_ACTION_BY_TEST_ID[dataTestId];
  if (!action) return null;

  return {
    menuSource: "global_add",
    action
  };
}

type TrackEvent = (eventName: string, eventProperties?: Record<string, unknown>) => void;

export function trackQuickActionMenuItem(trackEvent: TrackEvent, payload: QuickActionMenuAnalyticsPayload | null) {
  if (payload) {
    trackEvent(QUICK_ACTION_MENU_ANALYTICS_EVENTS.clickMenuItem, payload);
  }
}

export function withPersonContactMenuTracking(action: Action, pgaId: string, trackEvent: TrackEvent): Action {
  const { onClick, testId } = action;
  if (!onClick) return action;

  return {
    ...action,
    onClick: () => {
      trackQuickActionMenuItem(trackEvent, getPersonContactMenuAnalyticsPayload(testId, pgaId));
      onClick();
    }
  };
}

export function openTrackedGlobalAddMenuItem(dataTestId: string, openSidebar: () => void, trackEvent: TrackEvent) {
  return () => {
    trackQuickActionMenuItem(trackEvent, getGlobalAddMenuAnalyticsPayload(dataTestId));
    openSidebar();
  };
}
